import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  Copy,
  FileDown,
  Play,
  RefreshCcw,
  RotateCcw,
  ShieldAlert,
  UploadCloud
} from 'lucide-react'
import { useEffect, useState } from 'react'

import { FileDrop } from '../components/FileDrop'
import { ResultsTable } from '../components/ResultsTable'
import {
  completeUpload,
  createCase,
  createReport,
  fetchAdminJobs,
  fetchCases,
  fetchResults,
  presignUpload,
  retryAdminJob,
  type AdminJob,
  type ReportDownload,
  type SearchCase,
  runSearch,
  uploadToR2
} from '../lib/api'
import { useAuth } from '../lib/auth'

type FlowState = {
  caseId: string | null
  message: string
}

const legalDisclaimer =
  '本系统仅用于初步知识产权风险筛查。相似度分数不等于侵权概率，数据库未发现结果也不代表不存在商标权、普通法权利、版权、外观专利或其他权利。重要上架和争议事项应由美国知识产权律师复核。'

type DashboardPageProps = {
  initialCaseId?: string
}

export function DashboardPage({ initialCaseId }: DashboardPageProps) {
  const auth = useAuth()
  const navigate = useNavigate()
  const [file, setFile] = useState<File | null>(null)
  const [queryText, setQueryText] = useState('')
  const [flow, setFlow] = useState<FlowState>({
    caseId: initialCaseId ?? null,
    message: initialCaseId ? 'Loaded case from URL' : 'Ready'
  })
  const [latestReport, setLatestReport] = useState<ReportDownload | null>(null)
  const safeCaseId = flow.caseId
  const isAdmin = auth.status === 'authenticated' && auth.user.role === 'admin'
  const canWriteCases =
    auth.status === 'authenticated' &&
    (auth.user.role === 'admin' || auth.user.role === 'analyst')

  useEffect(() => {
    if (!initialCaseId) {
      return
    }
    setLatestReport(null)
    setFlow((current) => {
      if (current.caseId === initialCaseId) {
        return current
      }
      return { caseId: initialCaseId, message: 'Loaded case from URL' }
    })
  }, [initialCaseId])

  const casesQuery = useQuery({
    queryKey: ['cases'],
    queryFn: fetchCases
  })

  const resultsQuery = useQuery({
    queryKey: ['results', safeCaseId],
    queryFn: () => {
      if (!safeCaseId) {
        throw new Error('Missing case id')
      }
      const checkedCaseId = safeCaseId
      return fetchResults(checkedCaseId)
    },
    enabled: Boolean(safeCaseId)
  })

  const jobsQuery = useQuery({
    queryKey: ['admin-jobs'],
    queryFn: fetchAdminJobs,
    enabled: isAdmin
  })

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!canWriteCases) {
        throw new Error('Analyst or admin role is required')
      }
      if (!file) {
        throw new Error('Select an image first')
      }
      const safeFile = file
      const safeQueryText = queryText.trim()
      const caseTitle = safeQueryText || `Screening ${new Date().toISOString()}`
      const created = await createCase(caseTitle)
      const presign = await presignUpload({ caseId: created.id, file: safeFile })
      await uploadToR2({ presign, file: safeFile })
      await completeUpload({ caseId: created.id, assetId: presign.asset_id })
      await runSearch(created.id)
      return created.id
    },
    onSuccess: (caseId) => {
      setLatestReport(null)
      setFlow({ caseId, message: 'Search queued' })
      void navigate({ to: '/cases/$caseId', params: { caseId } })
      void casesQuery.refetch()
      if (isAdmin) {
        void jobsQuery.refetch()
      }
    },
    onError: (error: Error) => {
      setFlow({ caseId: null, message: error.message })
    }
  })

  const reportMutation = useMutation({
    mutationFn: async () => {
      if (!canWriteCases) {
        throw new Error('Analyst or admin role is required')
      }
      if (!safeCaseId) {
        throw new Error('Create a case first')
      }
      const checkedCaseId = safeCaseId
      return createReport(checkedCaseId)
    },
    onSuccess: (report) => {
      setLatestReport(report)
      setFlow((current) => ({ ...current, message: 'Report ready' }))
      window.open(report.download_url, '_blank', 'noopener,noreferrer')
    },
    onError: (error: Error) => {
      setFlow((current) => ({ ...current, message: error.message }))
    }
  })

  const retryMutation = useMutation({
    mutationFn: retryAdminJob,
    onSuccess: (job) => {
      setFlow((current) => ({
        ...current,
        message: `${formatJobType(job.job_type)} retry queued`
      }))
      void jobsQuery.refetch()
    },
    onError: (error: Error) => {
      setFlow((current) => ({ ...current, message: error.message }))
    }
  })

  const results = resultsQuery.data ?? []
  const jobs = jobsQuery.data ?? []
  const cases = casesQuery.data ?? []

  function copyCaseLink() {
    if (!safeCaseId) {
      return
    }
    if (!navigator.clipboard) {
      setFlow((current) => ({ ...current, message: 'Clipboard unavailable' }))
      return
    }
    const checkedCaseId = safeCaseId
    const caseUrl = new URL(`/cases/${checkedCaseId}`, window.location.origin).toString()
    void navigator.clipboard
      .writeText(caseUrl)
      .then(() => {
        setFlow((current) => ({ ...current, message: 'Case link copied' }))
      })
      .catch(() => {
        setFlow((current) => ({ ...current, message: 'Copy case link failed' }))
      })
  }

  return (
    <section className="screeningGrid">
      <div className="toolbar">
        <div>
          <p className="eyebrow">Class 14 screening</p>
          <h1>Image similarity review</h1>
        </div>
        <div className="toolbarActions">
          <button
            className="primaryButton"
            type="button"
            onClick={() => uploadMutation.mutate()}
            disabled={!canWriteCases || uploadMutation.isPending}
            title={canWriteCases ? 'Run search' : 'Analyst or admin role is required'}
          >
            {uploadMutation.isPending ? (
              <UploadCloud size={18} aria-hidden="true" />
            ) : (
              <Play size={18} aria-hidden="true" />
            )}
            <span>{uploadMutation.isPending ? 'Uploading' : 'Run Search'}</span>
          </button>
          <button
            className="secondaryButton"
            type="button"
            onClick={() => reportMutation.mutate()}
            disabled={!canWriteCases || !safeCaseId || reportMutation.isPending}
            title={canWriteCases ? 'Generate report' : 'Analyst or admin role is required'}
          >
            <FileDown size={18} aria-hidden="true" />
            <span>{reportMutation.isPending ? 'Generating' : 'Report'}</span>
          </button>
        </div>
      </div>

      <div className="workBand">
        <div className="uploadPane">
          <FileDrop file={file} onFileChange={setFile} disabled={!canWriteCases} />
          <label className="fieldLabel queryTextField">
            <span>Query text</span>
            <input
              type="text"
              value={queryText}
              onChange={(event) => {
                setQueryText(event.target.value)
              }}
              placeholder="Star charm pendant"
              disabled={!canWriteCases || uploadMutation.isPending}
            />
          </label>
          <div className="notice">
            <ShieldAlert size={18} aria-hidden="true" />
            <span>
              Results are preliminary screening signals and are not legal conclusions.
            </span>
          </div>
        </div>
        <div className="statusPane">
          <dl>
            <div>
              <dt>Status</dt>
              <dd>{flow.message}</dd>
            </div>
            <div>
              <dt>Case</dt>
              <dd>
                {flow.caseId ? (
                  <span className="caseValue">
                    <span>{flow.caseId}</span>
                    <button
                      aria-label="Copy case link"
                      className="iconButton"
                      type="button"
                      onClick={copyCaseLink}
                      title="Copy case link"
                    >
                      <Copy size={16} aria-hidden="true" />
                    </button>
                  </span>
                ) : (
                  'Not created'
                )}
              </dd>
            </div>
            <div>
              <dt>Top results</dt>
              <dd>{results.length}</dd>
            </div>
            <div>
              <dt>Report</dt>
              <dd>
                {latestReport ? (
                  <a href={latestReport.download_url} target="_blank" rel="noreferrer">
                    {latestReport.report_id}
                  </a>
                ) : (
                  'Not generated'
                )}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <CaseHistoryPanel
        cases={cases}
        activeCaseId={safeCaseId}
        isRefreshing={casesQuery.isFetching}
        error={casesQuery.error}
        onRefresh={() => {
          void casesQuery.refetch()
        }}
        onSelect={(selectedCase) => {
          setLatestReport(null)
          setFlow({
            caseId: selectedCase.id,
            message: `Loaded ${selectedCase.status} case`
          })
          void navigate({ to: '/cases/$caseId', params: { caseId: selectedCase.id } })
        }}
      />

      <div className="legalNotice" role="note">
        <ShieldAlert size={18} aria-hidden="true" />
        <span>{legalDisclaimer}</span>
      </div>

      <ResultsTable
        caseId={safeCaseId}
        results={results}
        canEditReview={canWriteCases}
        onReviewUpdated={() => {
          void resultsQuery.refetch()
        }}
      />
      {isAdmin ? (
        <AdminJobsPanel
          jobs={jobs}
          isLoading={jobsQuery.isLoading}
          isRefreshing={jobsQuery.isFetching}
          error={jobsQuery.error}
          retryingJobId={retryMutation.isPending ? retryMutation.variables ?? null : null}
          onRefresh={() => {
            void jobsQuery.refetch()
          }}
          onRetry={(jobId) => {
            retryMutation.mutate(jobId)
          }}
        />
      ) : null}
    </section>
  )
}

type CaseHistoryPanelProps = {
  cases: SearchCase[]
  activeCaseId: string | null
  isRefreshing: boolean
  error: Error | null
  onRefresh: () => void
  onSelect: (selectedCase: SearchCase) => void
}

function CaseHistoryPanel({
  cases,
  activeCaseId,
  isRefreshing,
  error,
  onRefresh,
  onSelect
}: CaseHistoryPanelProps) {
  return (
    <section className="casesPanel" aria-labelledby="cases-panel-title">
      <div className="casesHeader">
        <div>
          <p className="eyebrow">Search history</p>
          <h2 id="cases-panel-title">Recent Cases</h2>
        </div>
        <div className="casesActions">
          {error ? <span className="jobsError">{error.message}</span> : null}
          <span className="jobsMeta">{cases.length} recent</span>
          <button
            aria-label="Refresh cases"
            className="iconButton"
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing}
            title="Refresh cases"
          >
            <RefreshCcw size={16} aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="caseList">
        {cases.length > 0 ? (
          cases.slice(0, 8).map((searchCase) => {
            const isActive = activeCaseId === searchCase.id
            return (
              <button
                key={searchCase.id}
                className={isActive ? 'caseCard caseCard-active' : 'caseCard'}
                type="button"
                onClick={() => {
                  onSelect(searchCase)
                }}
              >
                <span>
                  <strong>{searchCase.title || 'Untitled case'}</strong>
                  <small>{searchCase.id}</small>
                </span>
                <span className="caseMeta">
                  <span className="statusPill">{searchCase.status}</span>
                  <time>{formatCaseDate(searchCase.created_at)}</time>
                </span>
              </button>
            )
          })
        ) : (
          <div className="casesEmpty">No cases yet</div>
        )}
      </div>
    </section>
  )
}

type AdminJobsPanelProps = {
  jobs: AdminJob[]
  isLoading: boolean
  isRefreshing: boolean
  error: Error | null
  retryingJobId: string | null
  onRefresh: () => void
  onRetry: (jobId: string) => void
}

function AdminJobsPanel({
  jobs,
  isLoading,
  isRefreshing,
  error,
  retryingJobId,
  onRefresh,
  onRetry
}: AdminJobsPanelProps) {
  return (
    <section className="jobsPanel" aria-labelledby="jobs-panel-title">
      <div className="jobsHeader">
        <div>
          <p className="eyebrow">Operations</p>
          <h2 id="jobs-panel-title">Jobs</h2>
        </div>
        <div className="jobsActions">
          {error ? <span className="jobsError">{error.message}</span> : null}
          <span className="jobsMeta">
            {isLoading ? 'Loading' : `${jobs.length} recent`}
          </span>
          <button
            aria-label="Refresh jobs"
            className="iconButton"
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing}
            title="Refresh jobs"
          >
            <RefreshCcw size={16} aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="tableFrame jobsTableFrame">
        <table className="jobsTable">
          <thead>
            <tr>
              <th>Type</th>
              <th>Status</th>
              <th>Attempts</th>
              <th>Run after</th>
              <th>Locked by</th>
              <th>Error</th>
              <th>Retry</th>
            </tr>
          </thead>
          <tbody>
            {jobs.length > 0 ? (
              jobs.map((job) => {
                const canRetry = isRetryableJobStatus(job.status)
                const isRetrying = retryingJobId === job.id
                return (
                  <tr key={job.id}>
                    <td>{formatJobType(job.job_type)}</td>
                    <td>
                      <span className={`jobStatus jobStatus-${job.status}`}>
                        {job.status.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      {job.attempts}/{job.max_attempts}
                    </td>
                    <td>{formatJobDate(job.run_after)}</td>
                    <td>{job.locked_by ?? '-'}</td>
                    <td className="jobErrorCell">{job.last_error ?? '-'}</td>
                    <td>
                      <button
                        aria-label={`Retry ${formatJobType(job.job_type)} job`}
                        className="iconButton"
                        type="button"
                        onClick={() => {
                          onRetry(job.id)
                        }}
                        disabled={!canRetry || isRetrying}
                        title={canRetry ? 'Retry job' : 'Only failed or dead jobs can be retried'}
                      >
                        <RotateCcw size={16} aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan={7} className="jobsEmpty">
                  No recent jobs
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function isRetryableJobStatus(status: string): boolean {
  return status === 'failed' || status === 'dead'
}

function formatJobType(value: string): string {
  return value.replace(/_/g, ' ')
}

function formatJobDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString()
}

function formatCaseDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString()
}
